import { IWaterWeeklyChartDataItem } from "../../types"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Text,
} from "recharts"
import { useCallback } from "react"
import styled from "styled-components"
import dayjs from "dayjs"
import { US_DATE_FORMAT_WITH_YEAR } from "config"
import ChartXAxisTick from "../ChartXAxisTick"

const roundUp = (value: number) => {
  const step = 500
  for (let index = 1; index < 1000; index++) {
    if (value < step * index) {
      return step * index
    }
  }
  return 0
}

const getMaxAmount = (data: IWaterWeeklyChartDataItem[]) => {
  return Math.max(...data.map((item) => item.amount))
}

export const getWaterMaxYAxis = (data: IWaterWeeklyChartDataItem[]) => {
  return roundUp(getMaxAmount(data))
}

const CustomWaterBarShape = (props: any) => {
  const { x, y, width, height, dimmed, selected } = props
  if (!height || height <= 0) return null

  const opacity = dimmed ? 0.2 : 1
  const r = Math.min(6, height / 2, width / 2)

  // Build rounded-top rect path: flat bottom, rounded top
  const d = `M${x},${y + height}V${y + r}A${r},${r},0,0,1,${x + r},${y}H${x + width - r}A${r},${r},0,0,1,${x + width},${y + r}V${y + height}Z`
  // Open path (no Z) so bottom edge is not stroked
  const borderPath = `M${x},${y + height}V${y + r}A${r},${r},0,0,1,${x + r},${y}H${x + width - r}A${r},${r},0,0,1,${x + width},${y + r}V${y + height}`

  return (
    <g opacity={opacity}>
      {selected && (
        <rect
          x={x - 2}
          y={y - 2}
          width={width + 4}
          height={height + 4}
          rx={r}
          ry={r}
          fill="white"
          filter="url(#waterBarGlow)"
        />
      )}
      <path d={d} fill="url(#waterBarGradient)" />
      {selected && (
        <path
          d={borderPath}
          fill="none"
          stroke="rgba(255, 255, 255, 0.2)"
          strokeWidth={1}
        />
      )}
    </g>
  )
}

const XAXIS_HEIGHT = 18
const TICK_FONT_SIZE = 10
const TOP_TICK_OFFSET = TICK_FONT_SIZE / 2

const ChartWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`

const WaterWeeklyBarChart = ({
  data,
  activeIndex,
  onBarClick,
}: {
  data: IWaterWeeklyChartDataItem[]
  activeIndex: number | null
  onBarClick: (index: number | null) => void
}) => {
  const maxAmount = getMaxAmount(data)
  const maxYAxis = roundUp(maxAmount)

  const handleChartClick = useCallback(
    (state: any) => {
      if (state?.activeTooltipIndex !== undefined) {
        onBarClick(state.activeTooltipIndex as number)
      } else {
        onBarClick(null)
      }
    },
    [onBarClick],
  )

  return (
    <ChartWrapper>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          width={500}
          height={300}
          data={data}
          margin={{
            top: 6,
            bottom: 0,
          }}
          barSize={34}
          onClick={handleChartClick}
        >
          <defs>
            <linearGradient id="waterBarGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4486DD" />
              <stop offset="100%" stopColor="#1852A1" />
            </linearGradient>
            <filter
              id="waterBarGlow"
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feGaussianBlur in="SourceGraphic" stdDeviation="8" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0.416  0 0 0 0 0.745  0 0 0 0 0.949  0 0 0 0.3 0"
              />
            </filter>
          </defs>

          <CartesianGrid
            vertical={false}
            stroke="rgba(255, 255, 255, 0.1)"
            strokeWidth={0.7}
          />

          <XAxis
            interval={0}
            height={XAXIS_HEIGHT}
            tickLine={false}
            scale="point"
            dataKey="name"
            padding={{ left: 16, right: 16 }}
            tick={(tickObject) => {
              const dimmed =
                activeIndex !== null && tickObject.index !== activeIndex
              const isToday =
                data[tickObject.index].date ===
                dayjs().format(US_DATE_FORMAT_WITH_YEAR)
              return (
                <ChartXAxisTick
                  tickProps={tickObject}
                  isToday={isToday}
                  dimmed={dimmed}
                />
              )
            }}
          />

          <YAxis
            domain={[0, maxYAxis]}
            tickMargin={0}
            axisLine={false}
            tickLine={false}
            orientation="right"
            width={40}
            stroke=""
            tick={(tickObject) => {
              const isTopTick = tickObject.index === (tickObject.visibleTicksCount ?? 0) - 1
              return (
                <Text {...tickObject} fill="#ffffffcc" width={26} fontSize={TICK_FONT_SIZE}
                  y={isTopTick ? tickObject.y - TOP_TICK_OFFSET : tickObject.y}>
                  {tickObject.payload.value}
                </Text>
              )
            }}
          />

          <Bar
            dataKey="amount"
            fill="url(#waterBarGradient)"
            shape={(props: any) => (
              <CustomWaterBarShape
                {...props}
                dimmed={activeIndex !== null && props.index !== activeIndex}
                selected={activeIndex !== null && props.index === activeIndex}
              />
            )}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  )
}

export default WaterWeeklyBarChart
